import { useState, KeyboardEvent, ChangeEvent } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { EmailEntry } from './types'
import { ErrorMessage } from '@/components/error-message'
import { RoleID } from '@/services/inference.gen'

type ManualEntryTabProps = {
  onEmailAdd: (email: EmailEntry) => { success: boolean; message: string }
}

export const ManualEntryTab = ({ onEmailAdd }: ManualEntryTabProps) => {
  const [state, setState] = useState({
    inputValue: '',
    userType: RoleID.User,
    errorMessage: '',
  })

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setState((prev) => ({
      ...prev,
      inputValue: e.target.value,
      errorMessage: '',
    }))
  }

  const onInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      onAddClick()
    }
  }

  const onUserTypeChange = (value: string) => {
    setState((prev) => ({ ...prev, userType: value as RoleID }))
  }

  const onAddClick = () => {
    if (!state.inputValue.trim()) return

    const result = onEmailAdd({
      email: state.inputValue.trim(),
      type: state.userType,
    })

    if (result.success) {
      setState((prev) => ({ ...prev, inputValue: '', errorMessage: '' }))
    } else {
      setState((prev) => ({ ...prev, errorMessage: result.message }))
    }
  }

  const onClearInput = () => {
    setState((prev) => ({ ...prev, inputValue: '', errorMessage: '' }))
  }

  return (
    <div className="space-y-4 py-4" data-testid="manual-entry-form">
      <div className="flex items-center gap-2">
        <div className="relative flex-grow">
          <Input
            type="email"
            placeholder="Enter email addresses"
            value={state.inputValue}
            onChange={onInputChange}
            onKeyDown={onInputKeyDown}
            className="pr-20"
            data-testid="email-input"
          />
          {state.inputValue && (
            <button
              onClick={onClearInput}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-foreground/40 hover:text-foreground/60"
              data-testid="clear-email-button"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <Select
          defaultValue={RoleID.User}
          value={state.userType}
          onValueChange={onUserTypeChange}
          data-testid="role-selector"
        >
          <SelectTrigger
            className="w-[100px]"
            data-testid="role-selector-trigger"
          >
            <SelectValue placeholder="Select role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={RoleID.User} data-testid="role-option-user">
              User
            </SelectItem>
            <SelectItem value={RoleID.Admin} data-testid="role-option-admin">
              Admin
            </SelectItem>
            <SelectItem
              value={RoleID.Manager}
              data-testid="role-option-manager"
            >
              Manager
            </SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={onAddClick} data-testid="add-email-button">
          Add
        </Button>
      </div>
      {state.errorMessage && <ErrorMessage message={state.errorMessage} />}
    </div>
  )
}
